import { useCallback, useEffect, useId, useRef } from 'react';
import { X } from 'lucide-react';
import Button from './Button';
import Heading from './Heading';

/**
 * Modal centrado para confirmaciones destructivas y formularios secundarios.
 * Reemplaza window.confirm y window.alert. Cierre por backdrop click, ESC,
 * o botón close (si dismissible).
 *
 * @param {Object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {'confirm'|'alert'} [props.variant='confirm']
 * @param {'default'|'danger'} [props.tone='default']
 * @param {string} props.title
 * @param {string|React.ReactNode} [props.description]
 * @param {string} [props.confirmLabel='Confirmar']
 * @param {string} [props.cancelLabel='Cancelar']
 * @param {() => void} [props.onConfirm]
 * @param {boolean} [props.dismissible=true]
 * @param {React.ReactNode} [props.children]  Si presente sustituye a description.
 */
export default function Modal({
  open,
  onClose,
  variant = 'confirm',
  tone = 'default',
  title,
  description,
  confirmLabel = 'Confirmar',
  cancelLabel = 'Cancelar',
  onConfirm,
  dismissible = true,
  children,
}) {
  const titleId = useId();
  const descId = useId();
  const cardRef = useRef(null);
  const previouslyFocused = useRef(null);

  // Bloquear scroll del body mientras abre.
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // ESC para cerrar (si dismissible). Foco inicial al primer botón
  // interactivo. Restauración del foco previo al cerrar.
  useEffect(() => {
    if (!open) return undefined;
    previouslyFocused.current = document.activeElement;

    const onKey = (e) => {
      if (e.key === 'Escape' && dismissible) {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);

    // Mover foco al primer botón del modal.
    const card = cardRef.current;
    const buttons = card ? card.querySelectorAll('button') : [];
    if (buttons.length > 0) {
      // En tone="danger" enfocamos el segundo (cancel) cuando hay dos CTAs,
      // para evitar confirmaciones destructivas accidentales con Enter.
      const target =
        tone === 'danger' && buttons.length >= 2 ? buttons[buttons.length - 2] : buttons[0];
      target.focus();
    }

    return () => {
      document.removeEventListener('keydown', onKey);
      const prev = previouslyFocused.current;
      if (prev && typeof prev.focus === 'function') {
        prev.focus();
      }
    };
  }, [open, dismissible, onClose, tone]);

  const handleBackdropClick = useCallback(() => {
    if (dismissible) onClose();
  }, [dismissible, onClose]);

  if (!open) return null;

  const primaryVariant = tone === 'danger' ? 'danger' : 'primary';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-ink/40 backdrop-blur-sm"
        onClick={handleBackdropClick}
        aria-hidden="true"
      />
      <div
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description || children ? descId : undefined}
        className="relative w-full max-w-md bg-paper border border-edge rounded-tk-lg shadow-tk-modal p-6"
      >
        <div className="flex items-start justify-between gap-3 mb-3">
          <Heading level="h2" editorial as="h2">
            <span id={titleId}>{title}</span>
          </Heading>
          {dismissible && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Cerrar"
              className="flex-shrink-0 text-ink-muted hover:text-ink transition-colors p-1 -m-1"
            >
              <X size={18} strokeWidth={1.6} />
            </button>
          )}
        </div>

        {(description || children) && (
          <div id={descId} className="text-body text-ink-muted font-sans mb-6">
            {children || description}
          </div>
        )}

        <div className="flex items-center justify-end gap-3">
          {variant === 'confirm' && (
            <Button variant="ghost" onClick={onClose}>
              {cancelLabel}
            </Button>
          )}
          <Button variant={primaryVariant} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
